"use client";

import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";

interface ChartOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  historicalData: Array<{ fecha: string; valor: number }>;
}

const ChartOverlay: React.FC<ChartOverlayProps> = ({
  isOpen,
  onClose,
  historicalData,
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!isOpen || !historicalData || historicalData.length === 0 || !chartRef.current) {
      return;
    }

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");
    if (!ctx) return;

    const sortedData = [...historicalData].sort((a, b) => {
      return new Date(a.fecha).getTime() - new Date(b.fecha).getTime();
    });

    const formattedDates = sortedData.map((row) => {
      const date = new Date(row.fecha);
      return date.toLocaleDateString("es-CL", { day: "numeric", month: "short" });
    });

    const values = sortedData.map((row) => row.valor);
    const minValue = Math.floor(Math.min(...values) / 10) * 10 - 10;
    const maxValue = Math.ceil(Math.max(...values) / 10) * 10 + 10;

    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(59, 130, 246, 0.4)");
    gradient.addColorStop(1, "rgba(59, 130, 246, 0)");

    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: formattedDates,
        datasets: [
          {
            label: "CLP",
            data: values,
            fill: true,
            borderColor: "#3b82f6",
            backgroundColor: gradient,
            borderWidth: 3,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 8,
            pointHoverBackgroundColor: "#3b82f6",
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 800,
          easing: "easeOutQuart",
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
            ticks: {
              color: "#64748b",
              font: {
                size: 12,
              },
              maxTicksLimit: 10,
            },
            border: {
              display: false,
            },
          },
          y: {
            min: minValue,
            max: maxValue,
            grid: {
              color: "#1e293b",
            },
            ticks: {
              color: "#64748b",
              font: {
                size: 12,
              },
              callback: function (value) {
                return "$" + value;
              },
            },
            border: {
              display: false,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "#0f172a",
            titleColor: "#f8fafc",
            bodyColor: "#10b981",
            bodyFont: {
              size: 16,
              weight: "bold",
            },
            padding: 16,
            cornerRadius: 12,
            displayColors: false,
            callbacks: {
              label: function (context) {
                return "$" + context.parsed.y.toLocaleString("es-CL") + " CLP";
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [isOpen, historicalData]);

  const sortedForCalc = historicalData && historicalData.length > 0
    ? [...historicalData].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())
    : [];

  const firstValue = sortedForCalc.length > 0 ? sortedForCalc[0]?.valor : null;
  const currentValue = sortedForCalc.length > 0 ? sortedForCalc[sortedForCalc.length - 1]?.valor : null;

  const change =
    currentValue && firstValue
      ? (((currentValue - firstValue) / firstValue) * 100).toFixed(2)
      : "0";

  const isPositive = parseFloat(change) >= 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-40 transition-opacity duration-500 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Chart Panel */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isOpen
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0"
        }`}
      >
        <div className="bg-gradient-to-t from-blue-950 via-blue-950 to-blue-950/95 rounded-t-[2.5rem] p-8 pb-12 max-h-[85vh] overflow-hidden border-t border-blue-800/50">
          {/* Handle bar */}
          <div className="flex justify-center mb-6">
            <div
              className="w-12 h-1.5 bg-blue-700 rounded-full cursor-pointer hover:bg-blue-600 transition-colors"
              onClick={onClose}
            />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-8 max-w-4xl mx-auto">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">
                Histórico del Dólar
              </h2>
              <p className="text-blue-400 text-sm">Últimos 30 días</p>
            </div>
            <div
              className={`px-4 py-2 rounded-full text-sm font-semibold ${
                isPositive
                  ? "bg-red-500/20 text-red-400"
                  : "bg-emerald-500/20 text-emerald-400"
              }`}
            >
              {isPositive ? "↑" : "↓"} {Math.abs(parseFloat(change))}%
            </div>
          </div>

          {/* Chart */}
          <div className="h-[50vh] max-w-4xl mx-auto">
            <canvas ref={chartRef} />
          </div>

          {/* Close hint */}
          <p
            className="text-center text-blue-500 text-sm mt-6 cursor-pointer hover:text-blue-300 transition-colors"
            onClick={onClose}
          >
            Toca para cerrar
          </p>
        </div>
      </div>
    </>
  );
};

export default ChartOverlay;
